import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useAppFlags } from 'utils/useAppFlags';
import React, { useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { compose } from 'redux';
import { Location as PatientLocation } from '@redux/device/deviceTypes';
import { getActiveOrganizationIdSelector } from '@redux/organization/organizationSelectors';
import { FEATURE_DISABLE_EMAIL_SUMMARIES, FEATURE_GAMIFICATION_EMAIL } from '@redux/organization/organizationTypes';
import { clearErrorPostNewPatientAction } from '@redux/person/personActions';
import { getErrorPostNewPatientSelector, getPersonByIdSelector, hasPatientCreatePermissionSelector, isLoadingPostNewPatientSelector } from '@redux/person/personSelector';
import { getPersonByIdThunk, getPersonFeatureThunk, postNewPatientThunk, putUpdatePatientThunk, setPersonFeatureThunk } from '@redux/person/personThunks';
import {
  GenderType,
  genderTypeList,
  LocationType,
  NewPatientRequest,
  Person,
  PronounType,
  PronounTypeList,
  UpdatePatientRequest,
  PatientType,
  PatientTypeLabels,
  PatientTypeList,
  USStateList
} from '@redux/person/personTypes';
import { ReduxState } from '@redux/types';
import { useAlert } from 'components/AlertInfoGlobal/useAlert';
import { Button } from '../../components/Button/Button';
import {
  CarePlanAutocomplete,
  CarePlanAutocompletePaper,
  CarePlanAutocompletePopper,
  CarePlanAutocompleteTextField as CarePlanTextField
} from '../../components/CarePlanAutocomplete/CarePlanAutocomplete';
import NavTopBarContentWrapper from '../../components/NavTopBarContentWrapper/NavTopBarContentWrapper';
import { TextField } from '../../components/TextField';
import { Text } from '../../components/Typography/Text';
import { customDateTimePickerStyles } from '../../customStyles';
import { convertIsoDateToLocalDate, toUtcMidnightIsoString } from '../../utils/dateInput';
import {
  birthDateAlertMessage,
  emailAlertMessage,
  getCleanPhoneNumber,
  getEnumKey,
  getFormattedPhoneNumber,
  isBirthDateValid,
  isEmailValid,
  isPhoneNumberValid,
  phoneNumberAlertMessage,
  trimObjectValues
} from '../../utils/util';
import style from './NewEditPatient.scss';

type PropsFromRedux = ConnectedProps<typeof connectRedux>;
type Props = PropsFromRedux & {};

export { NewEditPatient };

const personToNewPatientRequest = (person: Person): NewPatientRequest => {
  return {
    ...person,
    givenNames: person.givenNames ?? '',
    familyName: person.familyName ?? '',
    phoneNumber: getFormattedPhoneNumber(person.phoneNumber ?? ''),
    email: person.email ?? '',
    birthDate: person.birthDate ?? ''
  };
};

const emptyPatient: NewPatientRequest = {
  givenNames: '',
  familyName: '',
  phoneNumber: '',
  nickname: '',
  email: '',
  birthDate: '',
  genderType: null,
  pronounType: null,
  shippingAddress: {} as PatientLocation
};

const NewEditPatient: React.FC<Props> = ({
  hasPatientCreatePermission,
  isLoadingPostNewPatient,
  activeOrganizationId,
  postNewPatient,
  errorPostNewPatient,
  getPersonById,
  fetchPersonById,
  updatePatient,
  clearErrorPostNewPatient,
  getPersonFeature,
  setPersonFeature
}) => {
  const params = useParams();
  const patientId = params?.userId ?? '';
  const isCreateMode = !patientId;
  const [personFormData, setPersonFormData] = useState<NewPatientRequest>(emptyPatient);
  const [isDateInvalid, setIsDateInvalid] = useState(false);
  const [disableGamification, setDisableGamification] = useState(false);
  const [disableEmailSummaries, setDisableEmailSummaries] = useState(false);

  const navigate = useNavigate();
  const flags = useAppFlags();
  const { showAlertInfo, showAlertWarning } = useAlert();

  const [errors, setErrors] = useState({
    phoneNumber: '',
    email: '',
    birthDate: ''
  });

  // Reset any server error when the component is loaded
  useEffect(() => {
    clearErrorPostNewPatient();
    setErrors({ phoneNumber: '', email: '', birthDate: '' });
    return () => {
      clearErrorPostNewPatient();
    };
  }, []);

  useEffect(() => {
    patientId && fetchPersonById(patientId);
  }, [patientId]);

  useEffect(() => {
    if (!patientId) {
      return;
    }
    const person = getPersonById[patientId];
    setPersonFormData(person ? personToNewPatientRequest(person) : emptyPatient);
  }, [getPersonById, patientId]);

  // Load features status for edit mode
  useEffect(() => {
    if (patientId) {
      getPersonFeature(patientId, FEATURE_GAMIFICATION_EMAIL)
        .then((response) => {
          const data = response?.data || response;
          setDisableGamification(data?.disabled ?? false);
        })
        .catch(() => {
          // If feature doesn't exist, default to false (enabled)
          setDisableGamification(false);
        });
      getPersonFeature(patientId, FEATURE_DISABLE_EMAIL_SUMMARIES)
        .then((response) => {
          const data = response?.data || response;
          setDisableEmailSummaries(data?.disabled ?? false);
        })
        .catch(() => {
          setDisableEmailSummaries(false);
        });
    }
  }, [patientId, getPersonFeature]);

  const isSubmitBtnDisabled =
    isLoadingPostNewPatient ||
    !personFormData.givenNames ||
    !personFormData.familyName ||
    !personFormData.phoneNumber ||
    !personFormData.email ||
    !personFormData.birthDate ||
    !personFormData.genderType ||
    !personFormData.patientType ||
    isDateInvalid;

  useEffect(() => {
    if (errorPostNewPatient && errorPostNewPatient.status === 409) {
      const regex = new RegExp(`Email .* is already in use`, 'i');
      if (regex.test(errorPostNewPatient.message)) {
        setErrors({ ...errors, email: 'Email is already in use' });
      }
    }
  }, [errorPostNewPatient]);

  useEffect(() => {
    // clear stale server error when a new submission begins
    if (isLoadingPostNewPatient) {
      setErrors((prev) => ({ ...prev, email: '' }));
    }
  }, [isLoadingPostNewPatient]);

  const handlePersonChange = (e) => {
    const { name, value } = e.target;
    handlePersonDataChange(name, value);
  };

  const handleShippingAddressChange = (e) => {
    const { name, value } = e.target;
    handleShippingDataChange(name, value);
  };

  const handleShippingDataChange = (name, value) => {
    setPersonFormData({
      ...personFormData,
      shippingAddress: {
        ...personFormData.shippingAddress,
        [name]: value
      }
    });
  };

  const handlePhoneNumberChange = (e) => {
    const value = e.target.value;
    const formattedPhoneNumber = getFormattedPhoneNumber(value);
    handlePersonDataChange('phoneNumber', formattedPhoneNumber);
  };

  // Handle birth date changes, storing date as UTC midnight ISO string
  const handleBirthDateChange = (date: Date | null) => {
    handlePersonDataChange('birthDate', toUtcMidnightIsoString(date));
  };

  const handleGenderTypeChange = (event, value) => {
    const gender = getEnumKey(GenderType, value);
    handlePersonDataChange('genderType', gender);
  };

  const handlePatientTypeChange = (event, value) => {
    handlePersonDataChange('patientType', value);
  };

  const handlePronounTypeChange = (event, value) => {
    const pronoun = getEnumKey(PronounType, value);
    handlePersonDataChange('pronounType', pronoun);
  };

  const handleStateChange = (event, value) => {
    handleShippingDataChange('state', value);
  };

  const handlePersonDataChange = (name, value) => {
    setPersonFormData({
      ...personFormData,
      [name]: value
    });
  };

  const handleControlPeriodStartChange = (date: Date | null) => {
    const newDate = toUtcMidnightIsoString(date);
    setPersonFormData({
      ...personFormData,
      studyControlStartDate: newDate
    });
  };
  const handleControlPeriodEndChange = (date: Date | null) => {
    const newDate = toUtcMidnightIsoString(date);
    setPersonFormData({
      ...personFormData,
      studyControlEndDate: newDate
    });
  };
  const handleTreatmentPeriodStartChange = (date: Date | null) => {
    const newDate = toUtcMidnightIsoString(date);
    setPersonFormData({
      ...personFormData,
      studyTreatmentStartDate: newDate
    });
  };
  const handleTreatmentPeriodEndChange = (date: Date | null) => {
    const newDate = toUtcMidnightIsoString(date);
    setPersonFormData({
      ...personFormData,
      studyTreatmentEndDate: newDate
    });
  };

  const validateForm = () => {
    const newErrors = {
      phoneNumber: isPhoneNumberValid(personFormData.phoneNumber) ? '' : phoneNumberAlertMessage(personFormData.phoneNumber) || '',
      email: isEmailValid(personFormData.email) ? '' : emailAlertMessage(personFormData.email) || '',
      birthDate: personFormData.birthDate && !isBirthDateValid(personFormData.birthDate) ? birthDateAlertMessage(personFormData.birthDate) || '' : '',
      genderType: ''
    };

    setErrors(newErrors);

    return Object.values(newErrors).every((error) => error === '');
  };

  const handleFormSubmit = async (event) => {
    event.preventDefault();
    if (validateForm()) {
      const cleanPhoneNumber = getCleanPhoneNumber(personFormData.phoneNumber);
      try {
        if (isCreateMode) {
          const newPatientData = {
            ...trimObjectValues(personFormData),
            genderType: personFormData.genderType,
            pronounType: personFormData.pronounType,
            phoneNumber: cleanPhoneNumber,
            organizationId: activeOrganizationId // Pass active org for super admins
          } as unknown as NewPatientRequest;
          const createdPatient = await postNewPatient(newPatientData);
          const patientData = createdPatient?.data || createdPatient;

          // Set gamification feature if disabled
          if (disableGamification && patientData?.id) {
            await setPersonFeature(patientData.id, FEATURE_GAMIFICATION_EMAIL, disableGamification);
          }

          // Set email summaries feature if disabled
          if (disableEmailSummaries && patientData?.id) {
            await setPersonFeature(patientData.id, FEATURE_DISABLE_EMAIL_SUMMARIES, disableEmailSummaries);
          }

          showAlertInfo({
            message: 'Patient was successfully created!'
          });
          setPersonFormData(emptyPatient);
          setDisableGamification(false);
          setDisableEmailSummaries(false);
          return;
        }

        // Edit patient
        const patient = getPersonById[patientId];
        if (!patient || !patient.id) {
          showAlertWarning({
            message: 'Patient not found'
          });
          return;
        }
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { organizationId, ...patientFormDataWithoutOrg } = personFormData;
        const updatedPatientData: UpdatePatientRequest = {
          person: trimObjectValues({
            ...patient,
            ...patientFormDataWithoutOrg,
            shippingAddress: null
          }) as unknown as Person,
          shippingAddress: trimObjectValues({
            ...patient.shippingAddress,
            ...personFormData.shippingAddress,
            id: '',
            locationType: patient?.shippingAddress?.locationType ?? LocationType.SHIPPING_ADDRESS
          }) as unknown as PatientLocation,
          studyTreatmentStartDate: personFormData.studyTreatmentStartDate,
          studyTreatmentEndDate: personFormData.studyTreatmentEndDate,
          studyControlStartDate: personFormData.studyControlStartDate,
          studyControlEndDate: personFormData.studyControlEndDate
        };
        await updatePatient(updatedPatientData);

        // Update gamification feature
        await setPersonFeature(patientId, FEATURE_GAMIFICATION_EMAIL, disableGamification);

        // Update email summaries feature
        await setPersonFeature(patientId, FEATURE_DISABLE_EMAIL_SUMMARIES, disableEmailSummaries);

        showAlertInfo({
          message: 'Patient was successfully updated!'
        });
        fetchPersonById(patientId);
      } catch (err) {
        if (!isCreateMode || !(err as any)?.message.includes('status code 409')) {
          showAlertWarning({
            message: 'Something went wrong. Please try again.'
          });
        }
      }
    }
  };

  if (!hasPatientCreatePermission) {
    return null;
  }

  const birthDateValue = convertIsoDateToLocalDate(personFormData.birthDate);

  const controlPeriodStartDateValue = convertIsoDateToLocalDate(personFormData.studyControlStartDate);
  const controlPeriodEndDateValue = convertIsoDateToLocalDate(personFormData.studyControlEndDate);
  const treatmentPeriodStartDateValue = convertIsoDateToLocalDate(personFormData.studyTreatmentStartDate);
  const treatmentPeriodEndDateValue = convertIsoDateToLocalDate(personFormData.studyTreatmentEndDate);

  return (
    <NavTopBarContentWrapper centerAlignContent headerText={isCreateMode ? 'Create New Patient' : 'Edit Patient'}>
      <form onSubmit={handleFormSubmit} className={style.mainContainer}>
        <div className={style.patientInfoContainer}>
          <Text variant="xl">Patient info</Text>
          <br />
          <div className={style.subContainer}>
            <div className={style.formRow}>
              {/* FIRST NAME */}
              <TextField
                isMandatory
                name="givenNames"
                value={personFormData.givenNames}
                onChange={handlePersonChange}
                label="First name"
                placeholder="First name"
                autoComplete="firstName"
              />
              {/* LAST NAME */}
              <TextField
                isMandatory
                name="familyName"
                value={personFormData.familyName}
                onChange={handlePersonChange}
                label="Last name"
                placeholder="Last name"
                autoComplete="lastName"
              />
            </div>
            <div className={style.formRow}>
              {/* EMAIL */}
              {isCreateMode && (
                <TextField
                  isMandatory
                  name="email"
                  value={personFormData.email}
                  onChange={handlePersonChange}
                  label="Email address"
                  placeholder="Email address"
                  autoComplete="email"
                  error={errors.email}
                />
              )}

              {/* PHONE */}
              <TextField
                isMandatory
                name="phoneNumber"
                value={personFormData.phoneNumber}
                onChange={handlePhoneNumberChange}
                label="Mobile number"
                placeholder="+1 (555) 000-0000"
                autoComplete="phoneNumber"
                error={errors.phoneNumber}
              />
            </div>
            <div className={style.formRow}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <div className={style.subContainerEvenDistribution}>
                  <Text variant="sm" className={style.labelSelect}>
                    Date of birth<span> *</span>
                  </Text>
                  <DatePicker
                    value={birthDateValue}
                    onChange={(newValue) => handleBirthDateChange(newValue)}
                    disableFuture
                    maxDate={new Date()}
                    onError={(reason) => setIsDateInvalid(!!reason)}
                    sx={customDateTimePickerStyles}
                  />
                </div>
              </LocalizationProvider>
              {/* Patient type */}
              <div className={style.subContainerEvenDistribution}>
                <Text variant="sm" className={style.labelSelect}>
                  Patient Type<span> *</span>
                </Text>
                <CarePlanAutocomplete
                  options={PatientTypeList}
                  value={personFormData?.patientType || null}
                  getOptionLabel={(patientType) => PatientTypeLabels[patientType as PatientType]}
                  onChange={handlePatientTypeChange}
                  PaperComponent={CarePlanAutocompletePaper}
                  PopperComponent={CarePlanAutocompletePopper}
                  renderInput={(params) => <CarePlanTextField {...params} placeholder="Select" />}
                  renderOption={(props, option, state) => (
                    <li {...props} key={option as PatientType}>
                      <div>{PatientTypeLabels[option as PatientType]}</div>
                    </li>
                  )}
                />
              </div>
            </div>
            <div className={style.formRow}>
              {/* NICKNAME */}
              <TextField name="nickname" value={personFormData.nickname ?? ''} onChange={handlePersonChange} label="Nickname" placeholder="Nickname" autoComplete="nickname" />
            </div>
            <div className={style.formRow}>
              {/* GENDER */}
              <div className={style.subContainerEvenDistribution}>
                <Text variant="sm" className={style.labelSelect}>
                  Gender<span> *</span>
                </Text>
                <CarePlanAutocomplete
                  options={genderTypeList}
                  value={personFormData.genderType || null} // Ensure value is not undefined
                  getOptionLabel={(genderType) => GenderType[genderType as GenderType]}
                  onChange={handleGenderTypeChange}
                  PaperComponent={CarePlanAutocompletePaper}
                  PopperComponent={CarePlanAutocompletePopper}
                  renderInput={(params) => <CarePlanTextField {...params} placeholder="Select" />}
                  renderOption={(props, option, state) => (
                    <li {...props} key={option as GenderType}>
                      <div>{option as GenderType}</div>
                    </li>
                  )}
                />
              </div>
              {/* pronoun */}
              <div className={style.subContainerEvenDistribution}>
                <Text variant="sm" className={style.labelSelect}>
                  Pronouns
                </Text>
                <CarePlanAutocomplete
                  options={PronounTypeList}
                  value={personFormData.pronounType || null} // Ensure value is not undefined
                  getOptionLabel={(pronounType) => PronounType[pronounType as PronounType]}
                  onChange={handlePronounTypeChange}
                  PaperComponent={CarePlanAutocompletePaper}
                  PopperComponent={CarePlanAutocompletePopper}
                  renderInput={(params) => <CarePlanTextField {...params} placeholder="Select" />}
                  renderOption={(props, option, state) => (
                    <li {...props} key={option as PronounType}>
                      <div>{option as PronounType}</div>
                    </li>
                  )}
                />
              </div>
            </div>
            <div className={style.formRow}>
              {/* Disable Gamification */}
              <div className={style.subContainerEvenDistribution}>
                <Text variant="sm" className={style.labelSelect}>
                  Disable Gamification
                </Text>
                <CarePlanAutocomplete
                  options={[false, true]}
                  value={disableGamification}
                  getOptionLabel={(option) => ((option as boolean) ? 'Yes' : 'No')}
                  onChange={(event, value) => setDisableGamification((value as boolean) ?? false)}
                  PaperComponent={CarePlanAutocompletePaper}
                  PopperComponent={CarePlanAutocompletePopper}
                  renderInput={(params) => <CarePlanTextField {...params} placeholder="Select" />}
                  renderOption={(props, option, state) => (
                    <li {...props} key={(option as boolean).toString()}>
                      <div>{(option as boolean) ? 'Yes' : 'No'}</div>
                    </li>
                  )}
                />
              </div>
              {/* Disable Email Summaries */}
              <div className={style.subContainerEvenDistribution}>
                <Text variant="sm" className={style.labelSelect}>
                  Disable email summaries
                </Text>
                <CarePlanAutocomplete
                  options={[false, true]}
                  value={disableEmailSummaries}
                  getOptionLabel={(option) => ((option as boolean) ? 'Yes' : 'No')}
                  onChange={(event, value) => setDisableEmailSummaries((value as boolean) ?? false)}
                  PaperComponent={CarePlanAutocompletePaper}
                  PopperComponent={CarePlanAutocompletePopper}
                  renderInput={(params) => <CarePlanTextField {...params} placeholder="Select" />}
                  renderOption={(props, option, state) => (
                    <li {...props} key={(option as boolean).toString()}>
                      <div>{(option as boolean) ? 'Yes' : 'No'}</div>
                    </li>
                  )}
                />
              </div>
            </div>
          </div>
        </div>
        <div className={style.addressContainer}>
          <Text variant="xl">Address</Text>
          <br />
          <div className={style.subContainer}>
            <div className={style.formRow}>
              {/*ADDRESS LINE1 */}
              <TextField
                name="street"
                className={style.street}
                value={personFormData.shippingAddress?.street ?? ''}
                onChange={handleShippingAddressChange}
                label="Address line 1"
                placeholder="Street address or P.O. box"
                autoComplete="street"
              />
              {/* POSTAL CODE */}
              <TextField
                name="postalCode"
                className={style.postalCode}
                value={personFormData.shippingAddress?.postalCode ?? ''}
                onChange={handleShippingAddressChange}
                label="ZIP code"
                placeholder="ZIP code"
                autoComplete="postalCode"
              />
            </div>
            <div className={style.formRow}>
              {/*STREET SECOND LINE*/}
              <TextField
                name="streetSecondLine"
                value={personFormData.shippingAddress?.streetSecondLine ?? ''}
                onChange={handleShippingAddressChange}
                label="Address line 2"
                placeholder="Apt, suite, unit, building, floor"
                autoComplete="streetSecondLine"
              />
            </div>
            <div className={style.formRow}>
              {/* CITY */}
              <div className={style.subContainerEvenDistribution}>
                <TextField
                  name="city"
                  value={personFormData.shippingAddress?.city ?? ''}
                  onChange={handleShippingAddressChange}
                  label="City"
                  placeholder="City"
                  autoComplete="city"
                />
              </div>

              {/* STATE */}
              <div className={style.subContainerEvenDistribution}>
                <Text variant="sm" className={style.labelSelect}>
                  State
                </Text>
                <CarePlanAutocomplete
                  options={USStateList}
                  value={personFormData.shippingAddress?.state ?? ''} // Ensure value is not undefined
                  onChange={handleStateChange}
                  PaperComponent={CarePlanAutocompletePaper}
                  PopperComponent={CarePlanAutocompletePopper}
                  renderInput={(params) => <CarePlanTextField {...params} placeholder="Select" />}
                  renderOption={(props, option, state) => (
                    <li {...props} key={option as string}>
                      <div>{option as string}</div>
                    </li>
                  )}
                />
              </div>
            </div>
          </div>
        </div>
        {flags.alzheimersSleepStudy && (
          <div className={style.studyDatesContainer}>
            <Text variant="xl">Study Protocol Dates</Text>
            <br />
            <div className={style.subContainer}>
              <div className={style.formRow}>
                {/* {Control Period} */}
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Control Period Start"
                    value={controlPeriodStartDateValue}
                    onChange={(newValue) => handleControlPeriodStartChange(newValue)}
                    sx={customDateTimePickerStyles}
                  />
                </LocalizationProvider>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Control Period End"
                    value={controlPeriodEndDateValue}
                    onChange={(newValue) => handleControlPeriodEndChange(newValue)}
                    sx={customDateTimePickerStyles}
                  />
                </LocalizationProvider>
              </div>
              <div className={style.formRow}>
                {/* {Treatment Period} */}
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Treatment Period Start"
                    value={treatmentPeriodStartDateValue}
                    onChange={(newValue) => handleTreatmentPeriodStartChange(newValue)}
                    sx={customDateTimePickerStyles}
                  />
                </LocalizationProvider>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Treatment Period End"
                    value={treatmentPeriodEndDateValue}
                    onChange={(newValue) => handleTreatmentPeriodEndChange(newValue)}
                    sx={customDateTimePickerStyles}
                  />
                </LocalizationProvider>
              </div>
            </div>
          </div>
        )}

        <div className={style.submitButtonContainer}>
          <div>
            <Button className={style.buttonCancel} onClick={() => navigate(-1)}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" color="primary" disabled={isSubmitBtnDisabled} className={style.button}>
              Submit
            </Button>
          </div>
        </div>
      </form>
    </NavTopBarContentWrapper>
  );
};

const connectRedux = connect(
  (state: ReduxState) => ({
    isLoadingPostNewPatient: isLoadingPostNewPatientSelector(state),
    hasPatientCreatePermission: hasPatientCreatePermissionSelector(state),
    errorPostNewPatient: getErrorPostNewPatientSelector(state),
    getPersonById: getPersonByIdSelector(state),
    activeOrganizationId: getActiveOrganizationIdSelector(state)
  }),
  {
    postNewPatient: postNewPatientThunk,
    fetchPersonById: getPersonByIdThunk,
    updatePatient: putUpdatePatientThunk,
    clearErrorPostNewPatient: clearErrorPostNewPatientAction,
    getPersonFeature: getPersonFeatureThunk,
    setPersonFeature: setPersonFeatureThunk
  }
);

export default compose(connectRedux)(NewEditPatient);
